import { inject, Injectable } from '@angular/core';
import { ActionDisabledCondition, ActionItemOptions, UserAction } from '@mediahaven/shared';
import { ApiFragment, NavigationLink, RecordTypeStructure, SimilaritySearchGuard } from '@stylehaven/components';
import { Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { MediaHavenActionType } from '../../../entities/action-types.entity';
import { ActionLinkService } from '../../abstract/action-link.service.interface';

@Injectable({ providedIn: 'root' })
export class OpenSimilaritySearchActionService extends ActionLinkService {
    // Services
    private readonly similaritySearchGuard: SimilaritySearchGuard = inject(SimilaritySearchGuard);

    // Properties
    protected override action: MediaHavenActionType = MediaHavenActionType.SIMILARITY_SEARCH;
    private readonly similarityPluginActive: Observable<boolean> = this.similaritySearchGuard
        .hasAccess()
        .pipe(shareReplay(1));

    /* Similarity search
     * Available when:
     *  * SIMILARITY_SEARCH plugin is active on current organisation
     *  * Record is not processing
     *  * Record is not deleted
     *  * Record is of type DATA or DATA_FLAT
     *
     * Disabled when:
     *  * Never
     */
    public getUserAction(record: ApiFragment): Observable<UserAction> {
        return this.similarityPluginActive.pipe(map(isPluginActive => this.userActionBuilder(record, isPluginActive)));
    }

    public userActionBuilder(record: ApiFragment, isPluginActive: boolean): UserAction {
        const isNotDeleted: boolean = !record.isDeleted();
        const isNotProcessing: boolean = !record.isProcessing();
        const recordTypeStructure: RecordTypeStructure = record.getRecordStructure();
        const isDataOrFlatData: boolean =
            recordTypeStructure === RecordTypeStructure.Data || recordTypeStructure === RecordTypeStructure.DataFlat;

        const availableConditions: boolean[] = [isPluginActive, isNotProcessing, isNotDeleted, isDataOrFlatData];
        const disableConditions: ActionDisabledCondition[] = [];

        return new UserAction(availableConditions, disableConditions);
    }

    public getLabelKey(target: ApiFragment): string {
        return 'actions.similarity-search';
    }

    protected getLink(record: ApiFragment, actionItemOptions: ActionItemOptions): Observable<NavigationLink> {
        const route = `/similar/${record.getFragmentId()}`;

        return of({
            route,
            target: '_blank'
        });
    }
}
